/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import ConfirmDialog from '../components/activities/ConfirmDialog';
import ReactDOM from 'react-dom';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
// FullCalendar v5 vendor styles — without these the grid renders without
// borders / header backgrounds. scale-portal pulls these in via a global
// SCSS index; CM imports them locally so we don't touch a shared CSS file.
import '@fullcalendar/common/main.css';
import '@fullcalendar/daygrid/main.css';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import ActivityChip from '../components/activities/ActivityChip';
import {
  ACTIVITY_COLOR,
  activityRank,
  slotRank,
} from '../components/activities/activityDisplay';
import AddActivityForm from '../components/activities/AddActivityForm';
import useFocusedActivity from '../components/activities/useFocusedActivity';

const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];

// ----------------------------------------------------------------------------
// Time helpers (mirrors scale-portal getAmPm / convertToAMPM)
// ----------------------------------------------------------------------------
function formatTimeFromHHMM(hhmm) {
  if (!hhmm || typeof hhmm !== 'string' || !hhmm.includes(':')) return null;
  // If the string already carries an AM/PM suffix it's pre-formatted —
  // pass it through unchanged.  Re-parsing "1:30 PM" as HH:MM splits on
  // ":" and reads hour=1 / mins="30 PM"→"30", which silently flips PM
  // afternoon slots into "1:30 AM".  Detected by trailing AM|PM regardless
  // of case or surrounding whitespace.
  if (/\b[AP]M\b/i.test(hhmm)) return hhmm.trim();
  const [h, m] = hhmm.split(':');
  const hour = parseInt(h, 10);
  if (Number.isNaN(hour)) return null;
  const mins = (m || '00').slice(0, 2);
  const h12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${h12}:${mins} ${hour >= 12 ? 'PM' : 'AM'}`;
}

const bookingStatusTile = {
  not_available: 'Not Available',
  available: 'Available',
  booked: 'Booked',
};

// ----------------------------------------------------------------------------
// EventContent — mirrors scale-portal's
// components/template/ScheduleManagement/EventContent.jsx byte-for-byte
// (minus the coupon edit dropdown — CM doesn't need coupon editing).
//
// Additions:
//   - NoteIcon shown on hover / when notes exist (CM-only feature).
// ----------------------------------------------------------------------------
// Portal-rendered tooltip for the overlap-warning icon. Renders into
// document.body so the calendar cell's `overflow:hidden` can't clip it,
// matching scale-portal's Tooltip behavior. Hover-only, no click.
function OverlapWarning({ overlapEvents }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const anchorRef = useRef(null);
  const labels = Array.isArray(overlapEvents)
    ? overlapEvents.map((e) => e?.summary || e?.subject || '(untitled)')
    : [];
  if (labels.length === 0) return null;

  const handleEnter = () => {
    if (!anchorRef.current) return;
    const rect = anchorRef.current.getBoundingClientRect();
    const TOOLTIP_W = 240;
    setPos({
      top: rect.top - 8,                              // anchor above the icon
      left: Math.min(window.innerWidth - TOOLTIP_W - 8, rect.right - TOOLTIP_W),
    });
    setOpen(true);
  };

  return (
    <span
      ref={anchorRef}
      className="inline-flex items-center cursor-help"
      onMouseEnter={handleEnter}
      onMouseLeave={() => setOpen(false)}
    >
      <StatusWarningIcon />
      {open && pos && ReactDOM.createPortal(
        <div
          className="fixed z-[10000] bg-gray-900 text-white rounded-md shadow-xl"
          style={{
            top: pos.top,
            left: pos.left,
            transform: 'translateY(-100%)',
            padding: '8px 10px',
            width: 240,
            fontSize: 11,
            lineHeight: 1.45,
            pointerEvents: 'none',
            fontFamily: 'Inter, sans-serif',
          }}
        >
          <div className="font-bold underline mb-1">
            {labels.length > 1 ? 'Overlapping Events:' : 'Overlapping Event:'}
          </div>
          <ol style={{ listStyle: 'decimal', paddingLeft: 16, margin: 0 }}>
            {labels.map((label, i) => (
              <li key={i}>{label}</li>
            ))}
          </ol>
        </div>,
        document.body,
      )}
    </span>
  );
}

function StatusWarningIcon() {
  // Equivalent of scale's StatusIcon type="warning" text-yellow-400
  return (
    <svg
      className="w-4 h-4 text-yellow-400"
      viewBox="0 0 20 20"
      fill="currentColor"
      aria-hidden
    >
      <path
        fillRule="evenodd"
        d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 6a1 1 0 011 1v3a1 1 0 11-2 0V7a1 1 0 011-1zm0 8a1 1 0 100-2 1 1 0 000 2z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function getFullCaseName(caseName, additionalCaseNames) {
  if (!caseName) return '';
  if (!Array.isArray(additionalCaseNames) || additionalCaseNames.length === 0) {
    return caseName;
  }
  const extras = additionalCaseNames.filter(Boolean);
  if (extras.length === 0) return caseName;
  return `${caseName} +${extras.length}`;
}

/**
 * EventContent — visually identical to scale-portal/src/components/template/
 * ScheduleManagement/EventContent.jsx.  The wrapper classes, colour map,
 * spacing tokens and font weights are copied verbatim from that file.
 */
// ----------------------------------------------------------------------------
// SlotActionsMenu — a single three-dots menu per slot that manages BOTH slot
// notes and (on available slots) coupon/case-type assignment, so a slot never
// shows two separate dot icons. Coupons persist via the same availability
// write the "Open Monthly Availability" modal uses (POST /schedule/toggle with
// couponCodes). Rendered with fixed positioning so the day cell's
// overflow:hidden can't clip it.
// ----------------------------------------------------------------------------
const ThreeDotsIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
    <path fillRule="evenodd" d="M4.5 12a1.5 1.5 0 113 0 1.5 1.5 0 01-3 0zm6 0a1.5 1.5 0 113 0 1.5 1.5 0 01-3 0zm6 0a1.5 1.5 0 113 0 1.5 1.5 0 01-3 0z" clipRule="evenodd" />
  </svg>
);

function SlotActionsMenu({ status, hasNotes, onNoteClick, coupons = [], extendedProps, mediatorUserId, onCouponsUpdated }) {
  const { date, slot, couponCodes = [] } = extendedProps;
  const couponsEnabled = status === 'available' && coupons.length > 0;
  const [open, setOpen] = React.useState(false);
  const [pos, setPos] = React.useState({ top: 0, left: 0 });
  // Hide until measured so the flip/clamp reposition doesn't flash at the
  // initial (possibly off-screen) click point.
  const [ready, setReady] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const [updatingId, setUpdatingId] = React.useState(null);
  const [selectedIds, setSelectedIds] = React.useState(() => (couponCodes || []).map((c) => c.id));
  const btnRef = React.useRef(null);
  const panelRef = React.useRef(null);
  const pendingRefreshRef = React.useRef(false);

  // Keep local selection in sync with the slot's persisted coupons when not
  // mid-update (avoids clobbering an in-flight optimistic toggle).
  React.useEffect(() => {
    if (updatingId !== null) return;
    setSelectedIds((couponCodes || []).map((c) => c.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify((couponCodes || []).map((c) => c.id))]);

  React.useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (
        panelRef.current && !panelRef.current.contains(e.target) &&
        btnRef.current && !btnRef.current.contains(e.target)
      ) {
        closeMenu();
      }
    };
    const onEsc = (e) => { if (e.key === 'Escape') closeMenu(); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onEsc);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Open downward by default; flip above the trigger when the menu would
  // overflow the bottom of the viewport (e.g. the last calendar row) and there
  // is more room above. Then clamp fully on-screen.
  React.useLayoutEffect(() => {
    if (!open || !btnRef.current || !panelRef.current) return;
    const r = btnRef.current.getBoundingClientRect();
    const { height, width } = panelRef.current.getBoundingClientRect();
    const GUTTER = 8;
    const vh = window.innerHeight;
    const vw = window.innerWidth;
    let top = r.bottom + 4;
    const spaceBelow = vh - r.bottom;
    if (height && spaceBelow < height + GUTTER && r.top > spaceBelow) {
      top = r.top - height - 4;
    }
    if (height) top = Math.max(GUTTER, Math.min(top, vh - height - GUTTER));
    let left = Math.min(r.left, vw - 220);
    if (width) left = Math.max(GUTTER, Math.min(left, vw - width - GUTTER));
    setPos({ top, left });
    setReady(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const closeMenu = () => {
    setOpen(false);
    setReady(false);
    setSearch('');
    if (pendingRefreshRef.current && typeof onCouponsUpdated === 'function') {
      pendingRefreshRef.current = false;
      onCouponsUpdated();
    }
  };

  const toggleOpen = (e) => {
    e.stopPropagation();
    if (open) { closeMenu(); return; }
    const r = e.currentTarget.getBoundingClientRect();
    setPos({ top: r.bottom + 4, left: Math.min(r.left, window.innerWidth - 220) });
    setReady(false);
    setOpen(true);
  };

  const handleNotes = (e) => {
    e.stopPropagation();
    setOpen(false);
    // Anchor the notes popup to the three-dots button (not the menu row, which
    // lives in a body portal) so the notes modal opens in the exact same spot
    // the actions menu occupied.
    const anchor = btnRef.current;
    if (anchor) onNoteClick?.({ currentTarget: anchor, stopPropagation: () => {} });
    else onNoteClick?.(e);
  };

  const handleToggleCoupon = async (couponId) => {
    if (updatingId !== null || !mediatorUserId) return;
    const isChecked = selectedIds.includes(couponId);
    const nextIds = isChecked ? selectedIds.filter((id) => id !== couponId) : [...selectedIds, couponId];
    const prev = [...selectedIds];
    setSelectedIds(nextIds);
    setUpdatingId(couponId);
    const nextCouponCodes = coupons
      .filter((c) => nextIds.includes(c.id))
      .map((c) => ({ id: c.id, code: c.coupon_code }));
    try {
      await api.post('/schedule/toggle', {
        mediatorId: mediatorUserId,
        date,
        slot: slot === 'afternoon' ? 'afternoon' : 'morning',
        status: 'available',
        couponCodes: nextCouponCodes,
      });
      pendingRefreshRef.current = true;
    } catch (err) {
      console.error('Slot coupon update failed:', err);
      setSelectedIds(prev); // revert
    } finally {
      setUpdatingId(null);
    }
  };

  const filtered = coupons.filter((c) => (c.coupon_code || '').toLowerCase().includes(search.toLowerCase()));

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={toggleOpen}
        title="Slot actions (notes & case types)"
        className="relative flex items-center justify-center w-5 h-5 rounded text-gray-500 hover:bg-gray-200/60 transition-colors"
      >
        <ThreeDotsIcon />
        {hasNotes && <span className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 rounded-full bg-[#115e59]" />}
      </button>
      {open && ReactDOM.createPortal(
        <div
          ref={panelRef}
          className="fixed z-[9999] bg-white rounded-lg shadow-lg border border-gray-200 w-52 max-h-72 overflow-y-auto"
          style={{ top: pos.top, left: pos.left, visibility: ready ? 'visible' : 'hidden' }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Notes — available on every slot */}
          <button
            type="button"
            onClick={handleNotes}
            className="w-full flex items-center gap-2 px-3 py-2 text-xs text-left hover:bg-gray-50 border-b border-gray-100"
          >
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
              <path d="M2 2h12v10H6l-4 3V2z" stroke="#115e59" strokeWidth="1.2" strokeLinejoin="round" fill="none" />
              <path d="M5 6h6M5 9h4" stroke="#115e59" strokeWidth="1" strokeLinecap="round" />
            </svg>
            <span className="font-semibold text-gray-800">{hasNotes ? 'View / add notes' : 'Add note'}</span>
            {hasNotes && <span className="ml-auto w-1.5 h-1.5 rounded-full bg-[#115e59]" />}
          </button>

          {/* Coupons / case types — available slots only */}
          {couponsEnabled && (
            <>
              <div className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wide font-semibold text-gray-400">
                Case Types
              </div>
              <div className="px-2 pb-1">
                <input
                  type="text"
                  placeholder="Search..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full px-2 py-1 text-xs border rounded outline-none focus:border-[#115e59]"
                />
              </div>
              {filtered.length === 0 ? (
                <div className="px-3 py-2 text-xs text-red-500 font-semibold text-center">No record found.</div>
              ) : (
                filtered.map((c) => {
                  const rowBusy = updatingId === c.id;
                  return (
                    <label
                      key={c.id}
                      className={`flex items-center gap-2 px-3 py-1.5 text-xs cursor-pointer hover:bg-gray-50 ${updatingId !== null && !rowBusy ? 'opacity-60 pointer-events-none' : ''}`}
                    >
                      {rowBusy ? (
                        <span className="w-4 h-4 border-2 border-[#115e59] border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(c.id)}
                          onChange={() => handleToggleCoupon(c.id)}
                          disabled={updatingId !== null}
                          className="h-4 w-4 accent-[#115e59]"
                        />
                      )}
                      <span className="truncate font-semibold text-gray-800">{c.coupon_code}</span>
                    </label>
                  );
                })
              )}
            </>
          )}
        </div>,
        document.body
      )}
    </>
  );
}

function EventContent({ eventInfo, onEventClick, isDisabled, hasNotes, onNoteClick, coupons = [], mediatorUserId, onCouponsUpdated }) {
  const title = eventInfo.event.title;
  const [eventTime, eventStatus] = title.split(' - ');
  const extendedProps = eventInfo?.event?.extendedProps || {};
  const {
    case_id,
    status,
    case_name,
    additional_case_names,
    overlap_events,
    is_gsc_case,
    on_hold,
    hold_firm,
    hold_expires_at,
  } = extendedProps;

  // A held date is still status 'available' in the database, so it would
  // otherwise render as an ordinary open slot and invite a click that the
  // backend now refuses. Paint it and lock it here as well, so the board tells
  // the truth before anyone tries.
  const isHeld = on_hold === true;

  const handleEventClick = () => {
    if (isDisabled || isHeld) return;
    onEventClick(eventInfo);
  };

  const fullCaseName = getFullCaseName(case_name, additional_case_names);

  // Mirror scale-portal class composition exactly.
  const wrapperClasses = [
    'relative group w-full h-full outline-none cursor-pointer p-2 m-1 text-[11px] font-medium rounded gap-x-1',
    !isHeld && status === 'available' && '!bg-green-50 bg-opacity-20 !text-green-500',
    !isHeld && status === 'not_available' && '!bg-gray-100 !text-gray-900',
    !isHeld && status === 'booked' && '!bg-blue-50 bg-opacity-20 !text-blue-500',
    (isDisabled || isHeld) && 'cursor-auto',
    'flex justify-between items-center',
  ]
    .filter(Boolean)
    .join(' ');

  // The amber family the Scale calendar and schedule board already paint held
  // days with (#FBF1DD fill / #E3C489 border / #B45309 ink). Applied inline
  // rather than as Tailwind classes so the two products cannot drift to two
  // different ambers — these are the exact values from the portal's holdTheme.
  const heldStyle = isHeld
    ? {
        backgroundColor: '#FBF1DD',
        border: '1px solid #E3C489',
        color: '#B45309',
        // Set inline rather than relying on a trailing `cursor-auto` class:
        // equal-specificity Tailwind classes are resolved by stylesheet order,
        // not by their order in the class attribute, so the base
        // `cursor-pointer` would otherwise still win here.
        cursor: 'default',
      }
    : undefined;

  const heldTitle = isHeld
    ? [
        hold_firm ? `On hold for ${hold_firm}` : 'On hold',
        hold_expires_at ? `until ${hold_expires_at}` : null,
        '— release the hold in Scale to change this date',
      ]
        .filter(Boolean)
        .join(' ')
    : undefined;

  // When a case is booked, force the case label to dark text for legibility
  // against the blue-tinted booked background.  When the slot is just an
  // Available / Not Available marker, omit the colour override so the status
  // text inherits the wrapper colour (green-500 for available, gray-900 for
  // not_available) and matches the time label in the same row.
  // Inner status/case label.  When a case is booked we force gray-900 for
  // legibility against the blue-tinted background.  For an Available slot
  // we bump just the "Available" word to a darker green (green-700) +
  // bold — the time label stays in the wrapper's lighter green-500 so the
  // visual hierarchy reads time → status.  For Not Available we inherit
  // the wrapper's gray-900 and only add weight.
  const innerCaseClass = (case_id && case_name)
    ? 'w-full h-full inline-block break-words whitespace-normal !text-gray-900 !font-semibold'
    : status === 'available'
      ? '!text-green-500 !font-bold'
      : '!font-bold';

  return (
    <div className={wrapperClasses} style={heldStyle} title={heldTitle}>
      <div
        onClick={handleEventClick}
        className="flex gap-2 w-full min-w-0"
      >
        <span className="whitespace-nowrap">{eventTime}</span>
        <span className="min-w-0">
          <b className={isHeld ? 'w-full h-full inline-block break-words whitespace-normal font-bold' : innerCaseClass}>
            {/* Case label is the case name only (no case_number prefix,
                no coupon chip) — the user explicitly wants the CM cell to
                stay clean. GSC tag retained because it's an inherent
                case-type marker, not a coupon. */}
            {isHeld
              ? (hold_firm ? `On hold — ${hold_firm}` : 'On hold')
              : (fullCaseName || eventStatus)}
            {is_gsc_case && (
              <span
                className="ml-1 align-middle inline-flex items-center font-semibold text-white"
                style={{
                  backgroundColor: '#0d9488',
                  fontSize: 10,
                  borderRadius: 4,
                  padding: '0 4px',
                  lineHeight: 1.4,
                }}
              >
                GSC
              </span>
            )}
          </b>
        </span>
      </div>
      <div className="flex justify-end items-center gap-1">
        {Array.isArray(overlap_events) && overlap_events.length > 0 && status !== 'booked' && (
          <OverlapWarning overlapEvents={overlap_events} />
        )}
        {/* Single three-dots menu — manages BOTH notes (every slot) and
            coupons/case-types (available slots). Visible on hover, or always
            when notes exist (the dot badge signals existing notes). */}
        <div className={hasNotes ? 'block' : 'opacity-0 group-hover:opacity-100 transition-opacity'}>
          <SlotActionsMenu
            status={status}
            hasNotes={hasNotes}
            onNoteClick={onNoteClick}
            coupons={coupons}
            extendedProps={extendedProps}
            mediatorUserId={mediatorUserId}
            onCouponsUpdated={onCouponsUpdated}
          />
        </div>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------------
// formatEvent / generateEvents — port of scale-portal's
// utils/scheduleCalendarUtils.js, adapted to the CM BE response shape.
//
// CM BE returns:  { date: 'YYYY-MM-DD', period: 'AM'|'PM', slot_time: 'HH:MM',
//                   status: 'Available'|'Booked'|'Not Available'|...,
//                   case_id, case_number, case_name, is_gsc_case, couponCodes,
//                   overlap_events, additional_case_names, ... }
// ----------------------------------------------------------------------------
function normalizeStatus(s) {
  const lower = String(s || '').trim().toLowerCase();
  if (lower === 'available') return 'available';
  if (lower === 'booked') return 'booked';
  return 'not_available';
}

function formatEvent(displayStatus, dateStr, time, extendedProps) {
  // dateStr is YYYY-MM-DD; time is "HH:MM" (24h) or formatted "h:mm AM/PM"
  const timeLabel = (() => {
    const fromHHMM = formatTimeFromHHMM(time);
    if (fromHHMM) return fromHHMM;
    return time; // already formatted
  })();
  return {
    title: `${timeLabel} - ${displayStatus}`,
    start: `${dateStr}T00:00:00`,
    allDay: true,
    extendedProps: {
      date: dateStr,
      time,
      timeLabel,
      status: 'not_available',
      case_id: null,
      case_name: '',
      slot: '',
      ...extendedProps,
    },
  };
}

/**
 * Transform CM BE slots → FullCalendar events.  Mirrors scale-portal's
 * fetchEventsFromDatabase: groups by date+case_id, merges AM+PM bookings
 * with the same case into a single "fullday" event, and emits two slots
 * (morning + afternoon) for any weekday that has none.
 */
function buildEvents(slots, year, month, mediatorTimes) {
  const mt = mediatorTimes || {};
  const fallbackAm = formatTimeFromHHMM(mt.morning_time) || '8:00 AM';
  const fallbackPm = formatTimeFromHHMM(mt.afternoon_time) || '1:30 PM';
  const fallbackGscAm = formatTimeFromHHMM(mt.gsc_morning_time) || fallbackAm;
  const fallbackGscPm = formatTimeFromHHMM(mt.gsc_afternoon_time) || fallbackPm;
  const fallbackFullday = formatTimeFromHHMM(mt.fullday_time) || '9:00 AM - 5:00 PM';
  const fallbackGscFullday = formatTimeFromHHMM(mt.gsc_fullday_time) || fallbackFullday;

  // 1) Group BE events by date + case_id (only booked rows have a case_id)
  const groupedByCase = {};
  const standalone = [];
  slots.forEach((slot) => {
    const status = normalizeStatus(slot.status);
    if (status === 'booked' && slot.case_id) {
      const key = `${slot.date}-${slot.case_id}`;
      if (!groupedByCase[key]) groupedByCase[key] = [];
      groupedByCase[key].push(slot);
    } else {
      standalone.push(slot);
    }
  });

  const dbEvents = [];

  // Booked events: AM+PM same case → single fullday, else per-slot
  Object.values(groupedByCase).forEach((arr) => {
    if (arr.length === 2) {
      const a = arr[0];
      const isGsc = !!(a.is_gsc_case || arr[1].is_gsc_case);
      const fullTime = isGsc ? fallbackGscFullday : fallbackFullday;
      dbEvents.push(
        formatEvent(bookingStatusTile.booked, a.date, fullTime, {
          status: 'booked',
          case_id: a.case_id,
          case_number: a.case_number || null,
          case_name: a.case_name || null,
          additional_case_names: a.additional_case_names || [],
          is_gsc_case: isGsc,
          slot: 'fullday',
          couponCodes: dedupCoupons([
            ...(a.couponCodes || []),
            ...(arr[1].couponCodes || []),
          ]),
          overlap_events: [
            ...(a.overlap_events || []),
            ...(arr[1].overlap_events || []),
          ],
        })
      );
    } else {
      arr.forEach((slot) => {
        const period = (slot.period || '').toUpperCase();
        const slotName = period === 'AM' ? 'morning' : 'afternoon';
        const isGsc = !!slot.is_gsc_case;
        const time =
          formatTimeFromHHMM(slot.slot_time) ||
          (slotName === 'morning'
            ? isGsc
              ? fallbackGscAm
              : fallbackAm
            : isGsc
              ? fallbackGscPm
              : fallbackPm);
        dbEvents.push(
          formatEvent(bookingStatusTile.booked, slot.date, time, {
            status: 'booked',
            case_id: slot.case_id,
            case_number: slot.case_number || null,
            case_name: slot.case_name || null,
            additional_case_names: slot.additional_case_names || [],
            is_gsc_case: isGsc,
            slot: slotName,
            couponCodes: slot.couponCodes || [],
            overlap_events: slot.overlap_events || [],
          })
        );
      });
    }
  });

  // Non-booked rows (Available, Not Available)
  standalone.forEach((slot) => {
    const status = normalizeStatus(slot.status);
    const period = (slot.period || '').toUpperCase();
    const slotName = period === 'AM' ? 'morning' : 'afternoon';
    const time =
      formatTimeFromHHMM(slot.slot_time) ||
      (slotName === 'morning' ? fallbackAm : fallbackPm);
    dbEvents.push(
      formatEvent(bookingStatusTile[status], slot.date, time, {
        status,
        slot: slotName,
        availability_id: slot.id || slot.availability_id || null,
        overlap_events: slot.overlap_events || [],
        // Hold state must be carried explicitly: this object IS the event's
        // extendedProps, so anything omitted here never reaches the cell no
        // matter what the API returned. A held row arrives as status
        // 'available' and lands in this branch, so without these four the
        // board renders it as an ordinary open slot.
        on_hold: slot.on_hold === true,
        hold_type: slot.hold_type ?? null,
        hold_firm: slot.hold_firm ?? null,
        hold_expires_at: slot.hold_expires_at ?? null,
      })
    );
  });

  // 2) For each weekday in the visible month, fill missing AM/PM slots with
  //    "Not Available" placeholders so every weekday cell shows 2 events.
  //    Past dates are left blank (mirrors scale-portal — past slots can't be
  //    edited and don't need a placeholder; the cell just renders empty).
  const out = [];
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const today = new Date();
  const todayKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  for (let day = 1; day <= daysInMonth; day++) {
    const d = new Date(year, month, day);
    const dow = d.getDay(); // 0=Sun, 6=Sat
    if (dow === 0 || dow === 6) continue;
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const eventsForDay = dbEvents.filter((e) => e.extendedProps.date === dateStr);

    const hasFullday = eventsForDay.some((e) => e.extendedProps.slot === 'fullday');
    const hasAm = eventsForDay.some((e) => e.extendedProps.slot === 'morning');
    const hasPm = eventsForDay.some((e) => e.extendedProps.slot === 'afternoon');

    const isPast = dateStr < todayKey;

    // Past dates: render ONLY real "Booked" sessions (so historical
    // mediations stay visible) — suppress every Available / Not
    // Available placeholder, even if it came from the DB. Future +
    // today: render every real event AND fill any missing slot with a
    // "Not Available" placeholder so the user can toggle availability.
    if (isPast) {
      const bookedOnly = eventsForDay.filter(
        (e) => e.extendedProps?.status === 'booked'
      );
      out.push(...bookedOnly);
      continue;
    }

    if (hasFullday) {
      out.push(...eventsForDay);
      continue;
    }

    out.push(...eventsForDay);

    if (!hasAm) {
      out.push(
        formatEvent(bookingStatusTile.not_available, dateStr, fallbackAm, {
          status: 'not_available',
          slot: 'morning',
        })
      );
    }
    if (!hasPm) {
      out.push(
        formatEvent(bookingStatusTile.not_available, dateStr, fallbackPm, {
          status: 'not_available',
          slot: 'afternoon',
        })
      );
    }
  }

  // Sort by date asc then morning before afternoon
  out.sort((a, b) => {
    const da = a.extendedProps.date;
    const db = b.extendedProps.date;
    if (da !== db) return da < db ? -1 : 1;
    const sa = a.extendedProps.slot;
    const sb = b.extendedProps.slot;
    if (sa === sb) return 0;
    if (sa === 'fullday') return -1;
    if (sb === 'fullday') return 1;
    if (sa === 'morning') return -1;
    return 1;
  });

  return out;
}

function dedupCoupons(arr) {
  const seen = new Set();
  return arr.filter((c) => {
    const code = typeof c === 'string' ? c : c?.code;
    if (!code || seen.has(code)) return false;
    seen.add(code);
    return true;
  });
}

// ----------------------------------------------------------------------------
// Page
// ----------------------------------------------------------------------------
export default function Schedule() {
  const { user } = useAuth();
  const initials = ((user?.firstName || 'U')[0] + ((user?.lastName || '')[0] || '')).toUpperCase();
  const navigate = useNavigate();
  /* `/schedule?date=YYYY-MM-DD&mediatorId=<id>` — how the Activities list on a
   * case page sends the case manager to the day an activity sits on, for the
   * right mediator. Read once into the month/mediator state below. */
  const [searchParams] = useSearchParams();

  const calendarRef = useRef(null);
  const [mediatorTimes, setMediatorTimes] = useState(null);
  const [rawSlots, setRawSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(false);

  // Notes (CM-only)
  const [slotNotes, setSlotNotes] = useState({});
  const [activeNoteSlot, setActiveNoteSlot] = useState(null); // 'day-period'
  const [noteInput, setNoteInput] = useState('');
  const [notePopupPos, setNotePopupPos] = useState({ top: 0, left: 0 });
  const [savingNote, setSavingNote] = useState(false);
  const notePopupRef = useRef(null);
  // Hide the note popup until measured so the flip/clamp reposition doesn't
  // flash at the initial (possibly overflowing) click point.
  const [notePopupReady, setNotePopupReady] = useState(false);

  // Mediator dropdown
  const [mediators, setMediators] = useState([]);
  const [selectedMediator, setSelectedMediator] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  // Persisted across refresh so the Schedule screen keeps the selected mediator
  // (and therefore the dates being edited) instead of resetting to the first.
  const SCHEDULE_MEDIATOR_KEY = 'cm_schedule_selected_mediator';
  // Inline search so the picker stays usable for CMs with many mediators.
  const [mediatorQuery, setMediatorQuery] = useState('');

  // Month state (drives both BE fetch and FC navigation). Seeded from
  // `?date=` when present, so a deep link lands on the month holding that day;
  // read in the initialiser, not an effect, or paging away would be undone on
  // every render.
  const deepLinkDate = (() => {
    const v = searchParams.get('date');
    return /^\d{4}-\d{2}-\d{2}$/.test(v || '') ? v : null;
  })();
  const [currentMonth, setCurrentMonth] = useState(
    deepLinkDate ? Number(deepLinkDate.slice(5, 7)) - 1 : new Date().getMonth()
  );
  const [currentYear, setCurrentYear] = useState(
    deepLinkDate ? Number(deepLinkDate.slice(0, 4)) : new Date().getFullYear()
  );

  // Open Monthly Availability modal (mirrors scale-portal)
  const [isMonthlyOpenModal, setIsMonthlyOpenModal] = useState(false);
  const [bulkOpenLoading, setBulkOpenLoading] = useState(false);
  const [bulkOpenFeedback, setBulkOpenFeedback] = useState(null); // { type, text }
  // Coupons (a.k.a. "case types") for the currently selected mediator.  Drives
  // the Case types section of the Open Monthly Availability modal — mirrors
  // scale-portal/views/schedule/index.jsx 560-624.
  const [mediatorCoupons, setMediatorCoupons] = useState([]);
  // Selected entries the user wants the bulk-open to apply to.  Shape:
  // [{ value: <id>, label: <coupon_code> }, ...]
  const [selectedMonthlyCoupons, setSelectedMonthlyCoupons] = useState([]);

  // Availability Report modal (mirrors scale-portal)
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [reportMonthsAhead, setReportMonthsAhead] = useState(1);
  const [reportSelectedDays, setReportSelectedDays] = useState({
    Monday: true,
    Tuesday: true,
    Wednesday: true,
    Thursday: true,
    Friday: true,
  });
  const [reportLoading, setReportLoading] = useState(false);
  const [reportText, setReportText] = useState('');
  const [reportFeedback, setReportFeedback] = useState(null); // { type, text }
  const reportTextareaRef = useRef(null);

  const events = useMemo(
    () => buildEvents(rawSlots, currentYear, currentMonth, mediatorTimes),
    [rawSlots, currentYear, currentMonth, mediatorTimes]
  );

  // FC initial date for current month
  const initialDate = useMemo(
    () => `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-01`,
    [] // only once on mount
  );

  // Drive FC navigation when month state changes
  useEffect(() => {
    const api2 = calendarRef.current?.getApi();
    if (!api2) return;
    const target = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-01`;
    api2.gotoDate(target);
  }, [currentMonth, currentYear]);

  const goToPrevMonth = () => {
    if (currentMonth === 0) { setCurrentMonth(11); setCurrentYear((y) => y - 1); }
    else setCurrentMonth((m) => m - 1);
  };
  const goToNextMonth = () => {
    if (currentMonth === 11) { setCurrentMonth(0); setCurrentYear((y) => y + 1); }
    else setCurrentMonth((m) => m + 1);
  };

  // ---------------------------------------------------------------------------
  // Data fetching
  // ---------------------------------------------------------------------------
  useEffect(() => {
    api.get('/mediators?limit=200')
      .then((res) => {
        const list = (res.data?.data || []).filter((m) => m.name && m.name.trim());
        setMediators(list);
        if (list.length > 0 && !selectedMediator) {
          /* A `?mediatorId=` from a deep link outranks the saved choice —
           * otherwise the activity being opened belongs to one mediator and
           * the board shows another's month. Resolved HERE rather than in an
           * effect because the auto-pick below would otherwise win the race.
           * Matched against all three id shapes: the link carries the auth
           * uuid the case holds, while the saved key is the list `id`. */
          const wantedMediator = searchParams.get('mediatorId');
          const linked = wantedMediator
            ? list.find((m) =>
                [m.userId, m.mediatorId, m.id]
                  .filter((v) => v !== undefined && v !== null)
                  .some((v) => String(v) === String(wantedMediator))
              )
            : null;

          // Restore the last-selected mediator across refresh; fall back to the
          // first mediator when there's no saved choice (or it's no longer in
          // the list).
          let savedId = null;
          try { savedId = localStorage.getItem(SCHEDULE_MEDIATOR_KEY); } catch (_e) { /* storage unavailable */ }
          const match = savedId ? list.find((m) => String(m.id) === savedId) : null;
          setSelectedMediator(linked || match || list[0]);
        }
      })
      .catch(console.error);
  }, []);

  useEffect(() => {
    if (!selectedMediator) return;
    const mediatorParam = selectedMediator.userId || selectedMediator.mediatorId || selectedMediator.id;
    setLoading(true);
    api.get(`/schedule?mediatorId=${mediatorParam}&month=${currentMonth + 1}&year=${currentYear}`)
      .then((res) => {
        setRawSlots(res.data?.data || []);
        setMediatorTimes(res.data?.mediatorTimes || null);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [selectedMediator, currentMonth, currentYear]);

  /* ----------------------------------------------------------- activities --
   * Add Activity for the case manager.
   *
   * THE GATE IS PER MEDIATOR, NEVER PER CASE MANAGER (Usman, 6 Oct). Nikki
   * works across eleven mediators; she sees Add Activity on exactly those with
   * `booking_features.mediator_activities` set and not on the others. So this
   * is re-asked every time the mediator selector changes, and it fails CLOSED —
   * a lookup that errors hides the control rather than offering one whose save
   * would be refused. */
  /* Read straight off the selected mediator, which the picker already loaded.
   * This used to be a second request per mediator, so the Add Activity button
   * appeared a beat after the rest of the toolbar. Synchronous now — it paints
   * with its siblings. Strict === true, so anything else is off. */
  const activityAvailable = selectedMediator?.activityAvailable === true;
  const [activities, setActivities] = useState([]);
  /* null = closed. A string = open with that date pre-filled; '' = opened from
   * the header button, where the form falls back to today. */
  const [activityDate, setActivityDate] = useState(null);
  /* The activity being edited, or null — the dialog serves three doors and only
   * clicking an existing chip carries a row. */
  const [editingActivity, setEditingActivity] = useState(null);
  /* Awaiting a delete confirmation, or null. Deleting from the chip's "…" menu
   * must not route through the edit form first — that is a form to read for a
   * destructive action the case manager already chose. */
  const [deletingActivity, setDeletingActivity] = useState(null);
  const [isDeletingActivity, setIsDeletingActivity] = useState(false);
  /* Shown inside the confirm modal rather than as a toast: this page has no
   * toaster, and the message belongs next to the button that failed. */
  const [activityError, setActivityError] = useState('');
  const closeActivityDialog = () => {
    setActivityDate(null);
    setEditingActivity(null);
  };

  const mediatorParamOf = (m) => (m ? m.userId || m.mediatorId || m.id : null);

  /* Clicking the chip body opens the activity's case. `case_id` is `cases.id`,
   * which is what /cases/:id takes. A caseless activity never reaches here —
   * the chip gives it no click handler. */
  /* `?activity=<id>` — which chip the case manager clicked on the case page. */
  const focusedActivityId = useFocusedActivity();

  const openActivityCase = (activity) => {
    if (!activity?.case_id) return;
    /* `focus=activities` asks the case page to SCROLL to the Activities
       button and highlight it, rather than opening the dialog on arrival.
       Danish, 13 Oct: "it scrolls to that section and highlights it — it
       doesn't force you to look at it immediately. If you want them to
       immediately open that pop-up, first-timers will still not figure out
       where this pop-up came from." */
    navigate(`/cases/${activity.case_id}?focus=activities`);
  };

  const deleteActivity = async () => {
    if (!deletingActivity) return;
    setActivityError('');
    setIsDeletingActivity(true);
    try {
      await api.delete(`/activities/${deletingActivity.id}`, {
        params: { mediatorId: mediatorParamOf(selectedMediator) },
      });
      setDeletingActivity(null);
      loadActivities();
    } catch (err) {
      setActivityError(
        err?.response?.data?.message || 'The activity could not be deleted.'
      );
    } finally {
      setIsDeletingActivity(false);
    }
  };



  /* The month's activities. Kept in their OWN state and never merged into
   * `rawSlots`/`events`, which feed the availability tiles, the hiddenDays
   * calculation and the Open-All slot count. An activity has no slot and no
   * status; putting one in that array would either break those or render a
   * phantom availability tile. */
  const loadActivities = useCallback(async () => {
    const mediatorParam = mediatorParamOf(selectedMediator);
    if (!mediatorParam || !activityAvailable) {
      setActivities([]);
      return;
    }
    const from = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-01`;
    const last = new Date(currentYear, currentMonth + 1, 0).getDate();
    const to = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${last}`;
    try {
      const res = await api.get('/activities', {
        params: { mediatorId: mediatorParam, from, to },
      });
      setActivities(res?.data?.data || []);
    } catch (err) {
      /* An overlay on a calendar whose primary job is taking bookings — a
       * failed load must never take the schedule down with it. */
      console.error('activities load failed', err);
      setActivities([]);
    }
  }, [selectedMediator, activityAvailable, currentMonth, currentYear]);

  useEffect(() => {
    loadActivities();
  }, [loadActivities]);

  /* Grouped on `local_date`, a plain string the server already resolved in the
   * MEDIATOR's zone — not the case manager's and not the browser's. */
  const activitiesByDate = useMemo(() => {
    const map = {};
    activities.forEach((a) => {
      if (!a?.local_date) return;
      (map[a.local_date] = map[a.local_date] || []).push(a);
    });
    Object.values(map).forEach((list) =>
      list.sort((a, b) => {
        if (a.is_all_day !== b.is_all_day) return a.is_all_day ? -1 : 1;
        return String(a.local_time || '').localeCompare(String(b.local_time || ''));
      })
    );
    return map;
  }, [activities]);

  /* What FullCalendar is handed: availability PLUS activities.
   *
   * `events` itself is left untouched, and that is the point — other things
   * read it, and an activity reaching the availability logic could open a slot
   * on the strength of a phone call.
   *
   * POSITIONING: `allDay: true` at `${local_date}T00:00:00` — the EXACT shape
   * formatEvent gives every availability tile. That matters for layout, not
   * just tidiness: FullCalendar renders all-day events in aligned day-grid
   * rows and timed events in a separate section underneath, so an activity
   * carried as a timed event sat below a reserved block of empty row space and
   * left a visible gap under the slots. Same shape, same rows, tight stack.
   *
   * The string carries NO timezone offset, so FullCalendar parses it in the
   * calendar's own zone and it can never shift. The server already resolved
   * the day in the MEDIATOR's zone; recomputing it from `starts_at` against
   * the case manager's browser would move it.
   *
   * Ordering is handled by eventOrder below, which ranks an event with no
   * `slot` last — so activities fall under morning and afternoon without
   * needing a fake time to sort them. */
  const calendarEvents = useMemo(() => {
    if (!activityAvailable) return events;
    const activityEvents = Object.values(activitiesByDate)
      .flat()
      .map((activity) => ({
        id: `activity-${activity.id}`,
        title: activity.display_title || 'Activity',
        start: `${activity.local_date}T00:00:00`,
        allDay: true,
        display: 'block',
        extendedProps: { isActivity: true, activity },
      }));
    return [...events, ...activityEvents];
  }, [events, activitiesByDate, activityAvailable]);

  /* Sunday and Saturday stay hidden unless an activity lands on one. */
  const hiddenDays = useMemo(() => {
    if (!activityAvailable) return [0, 6];
    const worked = new Set();
    Object.keys(activitiesByDate).forEach((dateStr) => {
      const [y, m, d] = dateStr.split('-').map(Number);
      const dow = new Date(y, m - 1, d).getDay();
      if (dow === 0 || dow === 6) worked.add(dow);
    });
    return [0, 6].filter((d) => !worked.has(d));
  }, [activitiesByDate, activityAvailable]);



  useEffect(() => {
    if (!selectedMediator) return;
    const mediatorParam = selectedMediator.userId || selectedMediator.mediatorId || selectedMediator.id;
    api.get(`/schedule/notes?mediatorId=${mediatorParam}&month=${currentMonth + 1}&year=${currentYear}`)
      .then((res) => {
        const notesMap = {};
        const rawMap = res.data?.data || {};
        Object.entries(rawMap).forEach(([key, val]) => {
          if (val.notes?.length > 0) notesMap[key] = val.notes;
        });
        setSlotNotes(notesMap);
      })
      .catch(() => setSlotNotes({}));
  }, [selectedMediator, currentMonth, currentYear]);

  // Fetch the mediator's coupons (case types) once per mediator selection.
  // Powers the Case types section of the Open Monthly Availability modal.
  useEffect(() => {
    if (!selectedMediator) {
      setMediatorCoupons([]);
      setSelectedMonthlyCoupons([]);
      return;
    }
    const mediatorParam = selectedMediator.userId || selectedMediator.mediatorId || selectedMediator.id;
    api.get(`/schedule/coupons?mediatorId=${mediatorParam}`)
      .then((res) => {
        const list = res.data?.data || [];
        setMediatorCoupons(list);
        // Default to all selected (matches scale-portal's handleOpenMonthlyAvailabilityModal)
        setSelectedMonthlyCoupons(
          list.map((c) => ({ value: c.id, label: c.coupon_code }))
        );
      })
      .catch(() => {
        setMediatorCoupons([]);
        setSelectedMonthlyCoupons([]);
      });
  }, [selectedMediator]);

  // ---------------------------------------------------------------------------
  // Toggle handler — same BE call as before
  // ---------------------------------------------------------------------------
  const handleEventClick = useCallback(async (eventInfo) => {
    if (!selectedMediator || toggling) return;
    const { date, status, slot, on_hold, hold_firm, case_id } =
      eventInfo.event?.extendedProps || {};
    // A booked slot is a case, not an availability toggle — open it. Requested
    // by Rocio (2026-09-12): clicking a booked date on the CM schedule did
    // nothing, because this handler only ever toggled free slots. Same route
    // the Cases list uses (/cases/:id), so the detail page is reached
    // identically from either surface. Booked rows without a case_id (a
    // synthesised or legacy row) keep the old no-op rather than routing to a
    // broken page.
    if (status === 'booked') {
      if (case_id) navigate(`/cases/${case_id}`);
      return;
    }
    // Held dates are owned by Scale's inquiry flow. The backend refuses this
    // toggle, but stop it here too so the user gets an explanation rather than
    // a round-trip and a generic failure.
    if (on_hold === true) {
      alert(
        hold_firm
          ? `This date is on hold for ${hold_firm}. Release the hold in Scale before changing availability.`
          : 'This date is on hold. Release the hold in Scale before changing availability.'
      );
      return;
    }
    // Past dates are not editable (mirrors scale-portal).
    const todayKey = (() => {
      const t = new Date();
      return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
    })();
    if (date && date < todayKey) return;
    const mediatorId = selectedMediator.userId || selectedMediator.mediatorId || selectedMediator.id;
    const newStatus = status === 'available' ? 'not_available' : 'available';
    setToggling(true);
    try {
      await api.post('/schedule/toggle', {
        mediatorId,
        date,
        slot: slot === 'afternoon' ? 'afternoon' : 'morning',
        status: newStatus,
      });
      // Refetch the month's slots
      const res = await api.get(`/schedule?mediatorId=${mediatorId}&month=${currentMonth + 1}&year=${currentYear}`);
      setRawSlots(res.data?.data || []);
      setMediatorTimes(res.data?.mediatorTimes || null);
    } catch (err) {
      console.error('Toggle failed:', err);
      alert(err.response?.data?.message || 'Failed to update availability');
    } finally {
      setToggling(false);
    }
  }, [selectedMediator, toggling, currentMonth, currentYear, navigate]);

  // Refetch the visible month's slots without the full-page spinner — used by
  // the per-slot coupon dropdown after it persists a change.
  const refreshSlots = useCallback(async () => {
    if (!selectedMediator) return;
    const mediatorId = selectedMediator.userId || selectedMediator.mediatorId || selectedMediator.id;
    try {
      const res = await api.get(`/schedule?mediatorId=${mediatorId}&month=${currentMonth + 1}&year=${currentYear}`);
      setRawSlots(res.data?.data || []);
      setMediatorTimes(res.data?.mediatorTimes || null);
    } catch (err) {
      console.error('Refresh slots failed:', err);
    }
  }, [selectedMediator, currentMonth, currentYear]);

  // ---------------------------------------------------------------------------
  // Notes handlers
  // ---------------------------------------------------------------------------
  const handleOpenNotePopup = useCallback((day, period, e) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    const containerRect = e.currentTarget.closest('[data-cal-container]')?.getBoundingClientRect() || { left: 0, top: 0, width: 0, height: 0 };
    setNotePopupPos({
      top: rect.bottom - containerRect.top + 4,
      left: Math.min(rect.left - containerRect.left, (containerRect.width || 0) - 340),
      // Anchor + container bounds (container-relative) so the popup can flip
      // above the trigger when it would overflow the calendar's bottom edge.
      anchorTopRel: rect.top - containerRect.top,
      anchorBottomRel: rect.bottom - containerRect.top,
      containerHeight: containerRect.height || 0,
    });
    setNotePopupReady(false);
    setActiveNoteSlot(`${day}-${period}`);
    setNoteInput('');
  }, []);

  const handleAddNote = async () => {
    if (!noteInput.trim() || !activeNoteSlot || !selectedMediator || savingNote) return;
    const [day, period] = activeNoteSlot.split('-');
    const mediatorParam = selectedMediator.userId || selectedMediator.mediatorId || selectedMediator.id;
    const dateStr = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    setSavingNote(true);
    try {
      const res = await api.post('/schedule/notes', {
        mediatorId: mediatorParam,
        mediatorName: selectedMediator.name || '',
        date: dateStr,
        period: period.toUpperCase(),
        text: noteInput.trim(),
      });
      const savedNotes = res.data?.data?.notes || [];
      setSlotNotes((prev) => ({ ...prev, [activeNoteSlot]: savedNotes }));
      setNoteInput('');
      setActiveNoteSlot(null);
    } catch (err) {
      console.error('Failed to save note:', err);
    } finally {
      setSavingNote(false);
    }
  };

  useEffect(() => {
    if (!activeNoteSlot) return;
    const handleClickOutside = (e) => {
      if (e.target.closest('[data-note-popup]') || e.target.closest('[data-note-icon]')) return;
      setActiveNoteSlot(null);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [activeNoteSlot]);

  // After the note popup renders, measure it and flip it above the trigger when
  // it would overflow the calendar's bottom edge (e.g. the last row); then clamp
  // it within the container. Mirrors the slot-actions menu positioning.
  React.useLayoutEffect(() => {
    if (!activeNoteSlot || !notePopupRef.current) return;
    const { height } = notePopupRef.current.getBoundingClientRect();
    const GUTTER = 8;
    const ch = notePopupPos.containerHeight || 0;
    const aTop = notePopupPos.anchorTopRel ?? notePopupPos.top;
    const aBottom = notePopupPos.anchorBottomRel ?? notePopupPos.top;
    let top = aBottom + 4;
    if (ch && height) {
      const spaceBelow = ch - aBottom;
      if (spaceBelow < height + GUTTER && aTop > spaceBelow) {
        top = aTop - height - 4;
      }
      top = Math.max(GUTTER, Math.min(top, ch - height - GUTTER));
    }
    setNotePopupPos((p) => (p.top === top ? p : { ...p, top }));
    setNotePopupReady(true);
  }, [activeNoteSlot]);

  // ---------------------------------------------------------------------------
  // Open Monthly Availability — bulk-marks every NOT_AVAILABLE weekday slot
  // in the visible month as AVAILABLE. Mirrors scale-portal's
  // handleOpenMonthlyAvailability flow. No BE bulk endpoint exists on CM, so
  // we loop the existing /schedule/toggle per slot (matches the "data only,
  // no formula change" constraint).
  // ---------------------------------------------------------------------------
  const getCurrentMonthNotAvailableSlots = useCallback(() => {
    const anchorPrefix = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}`;
    const todayKey = (() => {
      const t = new Date();
      return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
    })();
    const seen = new Set();
    const out = [];
    events.forEach((ev) => {
      const ep = ev?.extendedProps || {};
      if (ep.status !== 'not_available') return;
      // Defensive: a hold normally sits on an 'available' row, so this filter
      // already excludes it — but never let a bulk open walk over a held date.
      if (ep.on_hold === true) return;
      if (!ep.date || !ep.date.startsWith(anchorPrefix)) return;
      // Skip past dates — they can't be edited
      if (ep.date < todayKey) return;
      // Skip weekends (already excluded by hiddenDays, defensive)
      const d = new Date(`${ep.date}T00:00:00`);
      const dow = d.getDay();
      if (dow === 0 || dow === 6) return;
      const slot = ep.slot === 'afternoon' ? 'afternoon' : 'morning';
      const key = `${ep.date}-${slot}`;
      if (seen.has(key)) return;
      seen.add(key);
      out.push({ date: ep.date, slot });
    });
    return out;
  }, [events, currentMonth, currentYear]);

  const monthlyBulkSlots = useMemo(
    () => (isMonthlyOpenModal ? getCurrentMonthNotAvailableSlots() : []),
    [isMonthlyOpenModal, getCurrentMonthNotAvailableSlots]
  );

  const handleOpenMonthlyAvailability = async () => {
    if (!selectedMediator || bulkOpenLoading) return;
    const slots = getCurrentMonthNotAvailableSlots();
    if (slots.length === 0) {
      setBulkOpenFeedback({ type: 'info', text: 'No not-available weekday slots found for this month.' });
      return;
    }
    const mediatorId = selectedMediator.userId || selectedMediator.mediatorId || selectedMediator.id;
    setBulkOpenLoading(true);
    setBulkOpenFeedback(null);
    try {
      // Loop the existing single-slot toggle. Run sequentially to avoid
      // overwhelming the BE / Supabase rate limits.  Pass the selected
      // couponCodes verbatim — server persists them on availability.couponCodes
      // (JSON column) so scale-portal sees the same case-type associations.
      // Also stamp is_available_by_mediator: true + proceeded_at: <utc now>
      // so the rows are byte-identical to what scale-portal's
      // apiBulkUpsertMediationSchedule writes.
      const couponCodesPayload = selectedMonthlyCoupons.map((opt) => ({
        id: opt.value,
        code: opt.label,
      }));
      const proceededAt = new Date().toISOString();
      // Track partial success so a mid-loop failure still triggers a
      // refetch (some slots got opened — the calendar must reflect that).
      let succeeded = 0;
      let firstError = null;
      for (const s of slots) {
        try {
          await api.post('/schedule/toggle', {
            mediatorId,
            date: s.date,
            slot: s.slot,
            status: 'available',
            couponCodes: couponCodesPayload,
            is_available_by_mediator: true,
            proceeded_at: proceededAt,
          });
          succeeded += 1;
        } catch (err) {
          if (!firstError) firstError = err;
          // Continue with remaining slots so one bad row doesn't halt the
          // bulk-open entirely — matches scale-portal's upsert behaviour.
        }
      }
      // Always refetch so the calendar reflects whatever made it to the DB,
      // even on partial failure.
      try {
        const res = await api.get(`/schedule?mediatorId=${mediatorId}&month=${currentMonth + 1}&year=${currentYear}`);
        setRawSlots(res.data?.data || []);
        setMediatorTimes(res.data?.mediatorTimes || null);
      } catch (_e) { /* refetch failures are non-fatal here */ }
      if (firstError) {
        setBulkOpenFeedback({
          type: 'error',
          text: firstError.response?.data?.message || `Failed on ${slots.length - succeeded} slot(s). ${succeeded} opened.`,
        });
      } else {
        setIsMonthlyOpenModal(false);
        setBulkOpenFeedback(null);
      }
    } catch (err) {
      console.error('Bulk open availability failed:', err);
      setBulkOpenFeedback({
        type: 'error',
        text: err.response?.data?.message || 'Failed to open monthly availability.',
      });
    } finally {
      setBulkOpenLoading(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Availability Report — fetches the next N months of slots and emits a
  // copy-pastable text block grouped by month, mirroring scale-portal's
  // AvailabilityReportModal default variant.
  // ---------------------------------------------------------------------------
  const generateAvailabilityReport = async () => {
    if (!selectedMediator || reportLoading) return;
    const hasSelectedDay = Object.values(reportSelectedDays).some(Boolean);
    if (!hasSelectedDay) {
      setReportFeedback({ type: 'warning', text: 'Please select at least one weekday.' });
      return;
    }
    const mediatorId = selectedMediator.userId || selectedMediator.mediatorId || selectedMediator.id;
    setReportLoading(true);
    setReportFeedback(null);
    try {
      // CM's /schedule endpoint takes month+year, so loop over the window
      // from today through reportMonthsAhead months forward.
      const today = new Date();
      const startKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
      const monthsToFetch = [];
      let y = today.getFullYear();
      let m = today.getMonth();
      for (let i = 0; i <= reportMonthsAhead; i++) {
        monthsToFetch.push({ year: y, month: m + 1 });
        m += 1;
        if (m > 11) { m = 0; y += 1; }
      }
      // End-of-window cutoff: last day of (current + reportMonthsAhead)
      const endY = today.getFullYear() + Math.floor((today.getMonth() + reportMonthsAhead) / 12);
      const endM = (today.getMonth() + reportMonthsAhead) % 12;
      const endLastDay = new Date(endY, endM + 1, 0).getDate();
      const endKey = `${endY}-${String(endM + 1).padStart(2, '0')}-${String(endLastDay).padStart(2, '0')}`;

      const collected = [];
      for (const { year, month } of monthsToFetch) {
        const res = await api.get(`/schedule?mediatorId=${mediatorId}&month=${month}&year=${year}`);
        const rows = res.data?.data || [];
        rows.forEach((r) => collected.push(r));
      }

      // Keep only AVAILABLE rows on selected weekdays inside the window
      const dayMap = { 1: 'Monday', 2: 'Tuesday', 3: 'Wednesday', 4: 'Thursday', 5: 'Friday' };
      const filtered = collected.filter((r) => {
        const status = String(r.status || '').trim().toLowerCase();
        if (status !== 'available') return false;
        if (!r.date) return false;
        if (r.date < startKey || r.date > endKey) return false;
        const dow = new Date(`${r.date}T00:00:00`).getDay();
        const dayKey = dayMap[dow];
        if (!dayKey) return false;
        return !!reportSelectedDays[dayKey];
      });

      // Roll up per-date AM/PM presence (period AM/PM or slot morning/afternoon)
      const byDate = {};
      filtered.forEach((r) => {
        const d = r.date;
        if (!byDate[d]) byDate[d] = { morning: false, afternoon: false };
        const period = String(r.period || '').toUpperCase();
        const slot = String(r.slot || '').toLowerCase();
        if (period === 'AM' || slot === 'morning') byDate[d].morning = true;
        if (period === 'PM' || slot === 'afternoon') byDate[d].afternoon = true;
        if (slot === 'fullday') { byDate[d].morning = true; byDate[d].afternoon = true; }
      });

      // Group by month name
      const monthGroups = {};
      Object.keys(byDate).sort().forEach((dateStr) => {
        const d = new Date(`${dateStr}T00:00:00`);
        const monthKey = MONTH_NAMES[d.getMonth()];
        if (!monthGroups[monthKey]) monthGroups[monthKey] = [];
        const day = d.getDate();
        const { morning, afternoon } = byDate[dateStr];
        let annotation = '';
        if (morning && afternoon) annotation = '';
        else if (afternoon) annotation = '(A)';
        else if (morning) annotation = '(M)';
        monthGroups[monthKey].push(`${day}${annotation}`);
      });

      const mediatorName = selectedMediator.name || 'Mediator';
      const now = new Date();
      const pad = (n) => String(n).padStart(2, '0');
      const hours12 = now.getHours() % 12 === 0 ? 12 : now.getHours() % 12;
      const ampm = now.getHours() >= 12 ? 'PM' : 'AM';
      const timestamp = `${pad(now.getMonth() + 1)}/${pad(now.getDate())}/${String(now.getFullYear()).slice(-2)} @ ${hours12}:${pad(now.getMinutes())}${ampm}`;
      let out = `${mediatorName} availability as of ${timestamp}\n\n`;
      if (Object.keys(monthGroups).length === 0) {
        out += 'No available dates found';
        setReportFeedback({ type: 'info', text: 'No available dates found for the selected criteria.' });
      } else {
        Object.keys(monthGroups).forEach((monthKey, i) => {
          if (i > 0) out += '\n';
          out += `${monthKey}: ${monthGroups[monthKey].join(', ')}\n`;
        });
        out += '\n(M): Morning\n(A): Afternoon';
        setReportFeedback({ type: 'success', text: 'Availability report generated successfully.' });
      }
      setReportText(out);
    } catch (err) {
      console.error('Failed to generate availability report:', err);
      setReportFeedback({ type: 'error', text: 'Error generating availability report.' });
    } finally {
      setReportLoading(false);
    }
  };

  const handleCopyReport = async () => {
    if (!reportText) return;
    try {
      await navigator.clipboard.writeText(reportText);
      setReportFeedback({ type: 'success', text: 'Report copied to clipboard!' });
    } catch {
      // Fallback: select the textarea
      const ta = reportTextareaRef.current;
      if (ta) {
        ta.focus();
        ta.select();
        try { document.execCommand('copy'); } catch { /* noop */ }
        setReportFeedback({ type: 'info', text: 'Report selected — press Cmd/Ctrl+C to copy.' });
      }
    }
  };

  // Reset Availability Report state every time the modal closes (matches scale)
  useEffect(() => {
    if (!isReportModalOpen) {
      setReportMonthsAhead(1);
      setReportSelectedDays({ Monday: true, Tuesday: true, Wednesday: true, Thursday: true, Friday: true });
      setReportText('');
      setReportFeedback(null);
      setReportLoading(false);
    }
  }, [isReportModalOpen]);

  // Reset bulk-open feedback when modal closes and re-prime the coupon
  // selection to all-selected every time the modal OPENS (mirrors
  // scale-portal handleOpenMonthlyAvailabilityModal).  Without this, any
  // deselections from a prior session linger.
  useEffect(() => {
    if (!isMonthlyOpenModal) {
      setBulkOpenFeedback(null);
      setBulkOpenLoading(false);
    } else {
      setSelectedMonthlyCoupons(
        mediatorCoupons.map((c) => ({ value: c.id, label: c.coupon_code }))
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMonthlyOpenModal]);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <div className="flex flex-col h-screen bg-white">
      {/* Top Header Bar - 60px (preserved from CM) */}
      <div className="flex items-center justify-between h-[60px] min-h-[60px] px-6 border-b border-[#e5e5e5]">
        <span className="text-[14px] font-semibold text-[#0e1e20] font-['Inter_Tight']" style={{ lineHeight: 1.5 }}>
          Schedule
        </span>
        <div className="flex items-center gap-4">
          <div className="flex items-center justify-center w-8 h-8 rounded-full bg-[#115e59]">
            <span className="text-[12px] font-semibold text-white font-['Inter']">{initials}</span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-auto bg-white">
        {/* Header: title + month nav (mirrors scale-portal CalendarHeader visually) */}
        <div className="px-8 pt-6 pb-4 flex flex-col gap-3">
          <div className="flex items-center justify-between md:flex-row flex-col gap-4">
            <div>
              <h2 className="text-xl font-semibold">Calendar</h2>
              <p className="text-sm font-normal pt-2">An overview of your calendar</p>
            </div>
            <div className="flex items-center gap-4 flex-wrap">
              {/* Add Activity — only for a mediator whose flag is on. The
                  gate is per MEDIATOR, so this appears and disappears as the
                  selector moves between them. */}
              {activityAvailable && (
                <button
                  onClick={() => setActivityDate('')}
                  disabled={!selectedMediator}
                  /* The SAME box as Availability Report below — filled teal,
                     white text, an icon that says what it opens. It was an
                     outlined button with a plus, which made the one new control
                     in the row the only one of its kind. "Activity", not "Add
                     Activity": it sits beside two other nouns. */
                  className="py-3 px-4 text-sm font-semibold rounded-lg flex items-center gap-2 bg-teal-700 hover:bg-teal-800 text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {/* The activity glyph used on the case page's Activities
                      button and on scale-portal's board. */}
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="5" width="18" height="16" rx="2" />
                    <path d="M3 10h18M8 3v4M16 3v4M12 14v4M10 16h4" />
                  </svg>
                  Activity
                </button>
              )}
              {/* Open Monthly Availability — bulk-marks every NOT_AVAILABLE
                  weekday slot in the visible month as AVAILABLE. Matches
                  scale-portal CalendarHeader's solid teal button. */}
              <button
                onClick={() => setIsMonthlyOpenModal(true)}
                disabled={!selectedMediator}
                className="py-3 px-4 text-sm font-semibold rounded-lg flex items-center gap-2 bg-slate-800 hover:bg-slate-900 text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Open Monthly Availability
              </button>
              {/* Availability Report — opens a modal that fetches the next
                  N months of available slots and emits a copy-pastable
                  text block. Matches scale-portal CalendarHeader's
                  "theme" variant button. */}
              <button
                onClick={() => setIsReportModalOpen(true)}
                disabled={!selectedMediator}
                className="py-3 px-4 text-sm font-semibold rounded-lg flex items-center gap-2 bg-teal-700 hover:bg-teal-800 text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="16" y1="13" x2="8" y2="13" />
                  <line x1="16" y1="17" x2="8" y2="17" />
                  <polyline points="10 9 9 9 8 9" />
                </svg>
                Availability Report
              </button>
              <div className="py-3 px-4 text-sm font-semibold bg-teal-800 rounded-lg text-white">
                {MONTH_NAMES[currentMonth]} {currentYear}
              </div>
              <div className="space-x-3">
                <button
                  onClick={goToPrevMonth}
                  className="p-3 rounded text-md font-semibold bg-white border border-gray-300 text-gray-500 inline-flex items-center justify-center"
                  aria-label="Previous month"
                >
                  <svg width="20" height="20" viewBox="0 0 16 16" fill="none">
                    <path d="M10 4L6 8L10 12" stroke="#6b7280" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
                <button
                  onClick={goToNextMonth}
                  className="p-3 rounded text-md font-semibold bg-white border border-gray-300 text-gray-500 inline-flex items-center justify-center"
                  aria-label="Next month"
                >
                  <svg width="20" height="20" viewBox="0 0 16 16" fill="none">
                    <path d="M6 4L10 8L6 12" stroke="#6b7280" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              </div>
            </div>
          </div>

          {/* Mediator selector + legend on a single row. Legend pills sit
              at the far right so the vertical space the legend used to
              occupy is reclaimed for the calendar. */}
          <div className="flex items-center gap-3">
            <span className="text-[13px] font-semibold text-[#333333] font-['Inter']">Mediator</span>
            <div className="relative w-[300px]">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-2.5 w-full h-10 px-3 rounded-lg border border-[#e8e8e8] bg-white text-left"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <circle cx="6" cy="5" r="2.67" stroke="#999" strokeWidth="1.33" fill="none" />
                  <path d="M1.33 14c0-2.5 2-4.5 4.67-4.5" stroke="#999" strokeWidth="1.33" strokeLinecap="round" fill="none" />
                  <circle cx="12.67" cy="5.5" r="1" stroke="#999" strokeWidth="1.33" fill="none" />
                  <path d="M10.67 14c0-1.5 1-3 2-3.5" stroke="#999" strokeWidth="1.33" strokeLinecap="round" fill="none" />
                </svg>
                <span className="text-[13px] font-medium text-[#333333] font-['Inter'] flex-1 truncate">
                  {selectedMediator ? selectedMediator.name : 'Select Mediator'}
                </span>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M4 6L8 10L12 6" stroke="#999" strokeWidth="1.33" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
              {dropdownOpen && (() => {
                const q = mediatorQuery.trim().toLowerCase();
                const filtered = q ? mediators.filter((m) => (m.name || '').toLowerCase().includes(q)) : mediators;
                return (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-[#e8e8e8] rounded-lg shadow-lg z-50 flex flex-col" style={{ maxHeight: 340 }}>
                    <div className="p-2 border-b border-[#e8e8e8]">
                      <div className="relative">
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" className="absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none">
                          <path d="M6.417 11.083A4.667 4.667 0 106.417 1.75a4.667 4.667 0 000 9.333zM12.25 12.25l-2.537-2.537" stroke="#9ca3af" strokeWidth="1.167" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        <input
                          type="text"
                          placeholder="Search mediators..."
                          value={mediatorQuery}
                          onChange={(e) => setMediatorQuery(e.target.value)}
                          autoFocus
                          className="w-full h-8 pl-8 pr-2 rounded-md border border-[#e8e8e8] bg-white text-[13px] text-[#0e1e20] placeholder-[#9ca3af] outline-none focus:border-[#115e59] focus:ring-1 focus:ring-[#115e59]/20"
                          style={{ fontFamily: 'Inter' }}
                        />
                      </div>
                    </div>
                    <div className="overflow-auto flex-1">
                      {filtered.length === 0 && (
                        <div className="px-3 py-3 text-[13px] text-[#999] font-['Inter']">No mediators found</div>
                      )}
                      {filtered.map((m) => (
                        <button
                          key={m.id}
                          onClick={() => {
                            setSelectedMediator(m);
                            try { localStorage.setItem(SCHEDULE_MEDIATOR_KEY, String(m.id)); } catch (_e) { /* storage unavailable */ }
                            setDropdownOpen(false);
                            setMediatorQuery('');
                          }}
                          className={`w-full text-left px-3 py-2.5 text-[13px] font-['Inter'] hover:bg-[#f5f5f5] transition-colors ${
                            selectedMediator?.id === m.id ? 'bg-[#e8fdf5] text-[#115e59] font-medium' : 'text-[#333333]'
                          }`}
                        >
                          {m.name}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* Legend pills — moved out of their own row to reclaim
                vertical space. Right-aligned via ml-auto. */}
            <div className="flex items-center gap-3 ml-auto">
              {activityAvailable && (
                /* NO DOT. Adam, 13 Oct: "why are we doing a purple dot here?
                   Nothing else has a dot… I think we can remove this dot from
                   the legend." Literally true of this row — every other pill
                   is a colour and a word. The pill keeps its colour, which is
                   what names the activity. */
                <div
                  className="text-xs font-semibold flex items-center px-3 py-1 rounded-full"
                  style={{ background: ACTIVITY_COLOR.bg, color: ACTIVITY_COLOR.fg }}
                >
                  <span>{ACTIVITY_COLOR.label}</span>
                </div>
              )}
              <div className="text-xs font-semibold flex items-baseline gap-2 bg-blue-50 px-3 py-1 rounded-full">
                <span className="text-blue-500">Booked</span>
              </div>
              <div className="text-xs font-semibold flex items-baseline gap-2 bg-green-50 px-3 py-1 rounded-full">
                <span className="text-green-600">Available</span>
              </div>
              <div className="text-xs font-semibold flex items-baseline gap-2 bg-gray-200 px-3 py-1 rounded-full">
                <span className="text-gray-900">Not Available</span>
              </div>
            </div>
          </div>
        </div>

        {/* Calendar — FullCalendar dayGridMonth, hiddenDays Sun/Sat */}
        <div className="px-8 pb-6 relative" data-cal-container>
          {/* Loading overlay — hides the calendar until slot data has
              actually arrived for the visible mediator/month. Avoids the
              empty-then-populated flash and keeps users from clicking
              cells before their state is known. */}
          {loading && (
            <div
              className="absolute inset-0 z-30 flex flex-col items-center justify-center bg-white/70"
              style={{ backdropFilter: 'blur(1px)' }}
              aria-live="polite"
            >
              <svg className="animate-spin" width="36" height="36" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" stroke="#e5e7eb" strokeWidth="3" />
                <path d="M22 12a10 10 0 0 0-10-10" stroke="#115e59" strokeWidth="3" strokeLinecap="round" />
              </svg>
              <span className="mt-3 text-[12px] font-medium text-[#5b5a64]" style={{ fontFamily: 'Inter' }}>
                Loading schedule…
              </span>
            </div>
          )}
          <div className="calendar rounded-md cm-fc-calendar">
            <FullCalendar
              ref={calendarRef}
              editable={false}
              plugins={[dayGridPlugin, interactionPlugin]}
              initialView="dayGridMonth"
              initialDate={initialDate}
              events={calendarEvents}
              eventContent={(eventInfo) => {
                /* Activities get their own renderer and must never reach
                   EventContent: that component reads availability-only
                   extendedProps and splits the title to recover a slot time,
                   which would mangle "Follow-up Call - Smith v. Jones". */
                if (eventInfo.event.extendedProps?.isActivity) {
                  return (
                    <div className="m-1 min-w-0">
                      <ActivityChip
                        activity={eventInfo.event.extendedProps.activity}
                        highlighted={
                          String(
                            eventInfo.event.extendedProps.activity?.id
                          ) === focusedActivityId
                        }
                        onOpenCase={openActivityCase}
                        onEdit={setEditingActivity}
                        onDelete={setDeletingActivity}
                      />
                    </div>
                  );
                }
                return (
                <EventContent
                  eventInfo={eventInfo}
                  onEventClick={handleEventClick}
                  isDisabled={toggling || loading}
                  hasNotes={(() => {
                    const ep = eventInfo.event?.extendedProps || {};
                    const day = parseInt(ep.date?.split('-')[2], 10);
                    if (!day) return false;
                    if (ep.slot === 'fullday') {
                      return (
                        (slotNotes[`${day}-am`] || []).length > 0 ||
                        (slotNotes[`${day}-pm`] || []).length > 0
                      );
                    }
                    const period = ep.slot === 'afternoon' ? 'pm' : 'am';
                    return (slotNotes[`${day}-${period}`] || []).length > 0;
                  })()}
                  onNoteClick={(e) => {
                    const ep = eventInfo.event?.extendedProps || {};
                    const day = parseInt(ep.date?.split('-')[2], 10);
                    const period = ep.slot === 'afternoon' ? 'pm' : 'am';
                    handleOpenNotePopup(day, period, e);
                  }}
                  coupons={mediatorCoupons}
                  mediatorUserId={selectedMediator?.userId || selectedMediator?.mediatorId || selectedMediator?.id}
                  onCouponsUpdated={refreshSlots}
                />
                );
              }}
              /* Per-day Add Activity. Passed ONLY when the feature is on:
                 supplying dayCellContent replaces FullCalendar's own
                 day-number render, and doing that for a case manager without
                 the feature would be a change for no reason. */
              dayCellContent={
                activityAvailable
                  ? (arg) => {
                      if (arg.isOther) return <span>{arg.dayNumberText}</span>;
                      const d = arg.date;
                      const dateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
                      return (
                        /* The "+" sits on the LEFT of the cell and the day
                           number on the right, as on the mediator's board.
                           FullCalendar's day-top is right-aligned, so this row
                           has to claim the full width itself. */
                        <span className="cm-day-top" style={{ display: 'flex', width: '100%', alignItems: 'center', justifyContent: 'space-between' }}>
                          {/* A BARE GLYPH, like the pencil in the "…" menu —
                              no box. Colour and background are NOT set here:
                              a Tailwind colour class would beat the :hover
                              rule below and the wash would never apply, which
                              is exactly how it broke in the mediator's portal.
                              The label is a positioned element, not `title`:
                              the native tooltip arrives a second late as
                              unstyled grey system text. */}
                          <span
                            role="button"
                            tabIndex={0}
                            aria-label="Add activity"
                            className="cm-add-activity relative inline-flex h-6 w-6 items-center justify-center rounded-md leading-none"
                            onClick={(e) => {
                              e.stopPropagation();
                              setActivityDate(dateStr);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                e.stopPropagation();
                                setActivityDate(dateStr);
                              }
                            }}
                          >
                            <span className="cm-add-activity-label">Add activity</span>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                              <path d="M12 5v14M5 12h14" />
                            </svg>
                          </span>
                          <span>{arg.dayNumberText}</span>
                        </span>
                      );
                    }
                  : undefined
              }
              headerToolbar={false}
              /* A weekend activity has to un-hide its day, or it is saved,
                 confirmed, and then simply not on the calendar. Only when the
                 feature is on, so every other case manager keeps the exact
                 Mon-Fri grid they have now. */
              hiddenDays={hiddenDays}
              fixedWeekCount={false}
              dayHeaderFormat={{ weekday: 'short' }}
              // height="auto" lets the grid size to its content (rows hug
              // their events). A FIXED height + expandRows uses FullCalendar's
              // "liquid-absolute" scroller, which measures available height to
              // fill — but our scroller CSS overrides (overflow:visible +
              // position:relative, written for height='auto') break that
              // measurement so TableRow.updateSizing never converges and loops
              // ("Maximum update depth exceeded"), most visibly on month change.
              // Auto height removes the liquid scroller entirely → no loop.
              height="auto"
              expandRows={false}
              // Force morning slot (8:00 AM) above afternoon (1:30 PM).
              // All events share the same start (`T00:00:00`) so FC's
              // default time-sort doesn't help — we sort on the slot
              // extendedProp instead. fullday wins, then morning, then
              // afternoon, then anything else.
              /* CHRONOLOGICAL, across bookings AND activities.
                 Every event on this board is `allDay` — availability slots as
                 well as activities — so FullCalendar's own start-time sort
                 cannot separate them, and a timed activity written as a timed
                 event sorts BELOW every all-day one instead of among them.
                 So the order is computed here, from the same ranks
                 activityDisplay uses: an activity with no time ranks below
                 every clock time and sits at the head of the day, a timed one
                 lands at its own minute, and a slot lands at its own.
                 Adam, 10 Oct: "it should always be like chronological... if
                 this is at 1:30pm then it should show after the 10am." */
              eventOrder={(a, b) => {
                if (!activityAvailable) {
                  const rank = (s) => (s === 'fullday' ? 0 : s === 'morning' ? 1 : s === 'afternoon' ? 2 : 3);
                  return rank(a?.extendedProps?.slot) - rank(b?.extendedProps?.slot);
                }
                const rankOf = (e) => {
                  const ep = e?.extendedProps || {};
                  if (ep.isActivity) return activityRank(ep.activity);
                  return slotRank(ep);
                };
                return rankOf(a) - rankOf(b);
              }}
            />
          </div>

          {/* Inline styling to mirror scale-portal's _full-calendar.css overrides
              without polluting the global stylesheet. */}
          <style>{`
            /* Per-day Add Activity: hidden until the day is hovered, so a plus
               on every cell does not compete with the availability tiles.
               Shown on keyboard focus too, and always on a touch device, which
               never hovers. */
            /* Motion copied from the Support launcher, so the two moving
               things in this product move the same way: a 220ms rise on
               appearing, a 120ms lift on hover, a press that scales to .97. */
            /* The day-top must span the cell, or its right-aligned parent
               keeps both children pinned to the right edge. */
            .cm-fc-calendar .fc-daygrid-day-top:has(.cm-day-top) { width: 100%; }
            .cm-fc-calendar .fc-daygrid-day-number:has(.cm-day-top) {
              width: 100%; box-sizing: border-box;
            }
            .cm-day-top { min-width: 0; }
            .cm-add-activity {
              opacity: 0; color: #6b7280; background: transparent; border: 0;
              transition: opacity 120ms ease-out, background-color 120ms ease-out,
                          color 120ms ease-out, transform 120ms ease-out,
                          box-shadow 120ms ease-out;
            }
            .fc-daygrid-day:hover .cm-add-activity,
            .cm-add-activity:focus-visible {
              opacity: 1;
              animation: cm-add-activity-in 220ms cubic-bezier(.16, 1, .3, 1) backwards;
            }
            @keyframes cm-add-activity-in {
              from { transform: translateY(4px); opacity: 0; }
              to   { transform: translateY(0);   opacity: 1; }
            }
            /* The same #f3f4f6 the "..." menu's rows use, so an icon and the
               row beside it behave identically. */
            .cm-add-activity:hover,
            .cm-add-activity:focus-visible {
              background: #f3f4f6; color: #374151;
              transform: translateY(-1px);
              box-shadow: 0 1px 2px rgba(17,24,39,.08), 0 4px 10px rgba(17,24,39,.10);
            }
            .cm-add-activity:active { transform: scale(.97); }
            .cm-add-activity-label {
              position: absolute; left: calc(100% + 7px); top: 50%;
              background: #fff; color: #1F5C50; font-size: 11px; font-weight: 500;
              padding: 4px 9px; border-radius: 999px; border: 1px solid #DCE7E2;
              box-shadow: 0 2px 7px rgba(24,33,31,.08);
              white-space: nowrap; pointer-events: none; opacity: 0;
              transform: translate(-5px, -50%);
              transition: opacity 160ms ease, transform 160ms ease; z-index: 30;
            }
            .cm-add-activity:hover .cm-add-activity-label,
            .cm-add-activity:focus-visible .cm-add-activity-label { opacity: 1; transform: translate(0, -50%); }
            @media (hover: none) { .cm-add-activity { opacity: 1; } }

            .cm-fc-calendar { height: 100%; }
            .cm-fc-calendar .fc { height: 100%; font-family: 'Inter', system-ui, sans-serif; }
            .cm-fc-calendar .fc-theme-standard .fc-scrollgrid { border: 0; }
            .cm-fc-calendar .fc-theme-standard td,
            .cm-fc-calendar .fc-theme-standard th { border-color: #e5e7eb; }
            .cm-fc-calendar .fc-theme-standard td:last-child,
            .cm-fc-calendar .fc-theme-standard th:last-child { border-right: 0; }
            .cm-fc-calendar .fc-col-header-cell {
              background-color: #115e59 !important;
              color: #ffffff !important;
              letter-spacing: 0.05em;
              border-width: 0 0 1px;
            }
            .cm-fc-calendar .fc .fc-col-header-cell-cushion {
              color: #ffffff;
              padding: 0.4rem 0;
              font-weight: 600;
              font-size: 12px;
            }
            .cm-fc-calendar .fc-view {
              border-radius: 0.5rem;
              border: 1px solid #e5e7eb;
              background-color: #ffffff;
              overflow: hidden;
            }
            .cm-fc-calendar .fc .fc-daygrid-day-number {
              font-weight: 600;
              padding: 0.25rem 0.4rem;
              font-size: 12px;
              color: #1f2937;
            }
            .cm-fc-calendar .fc-day-today { background-color: #e5e7eb !important; }
            /* Remove the calendar's internal scrollbar — with
               height='auto' + expandRows the grid sizes to fit all
               events, so the fc-scroller's overflow would otherwise
               still render a thin scrollbar even when there's nothing
               to scroll. Force visible overflow on both axes. */
            .cm-fc-calendar .fc-scroller,
            .cm-fc-calendar .fc-scroller-liquid-absolute {
              overflow: visible !important;
              position: relative !important;
            }
            .cm-fc-calendar .fc-scroller-harness { overflow: visible !important; }
            /* Day cells must clip content so the booking badge (with its
               long case-name label) can't spill into the neighboring day
               column. width:100% + min-width:0 forces the badge to wrap /
               truncate inside the cell. */
            .cm-fc-calendar .fc-daygrid-day-frame {
              overflow: hidden;
              padding: 2px;
              /* With height="auto" the grid no longer stretches rows to a fixed
                 height, so give each day cell a sensible minimum so the month
                 grid still reads as a full calendar instead of collapsing. */
              min-height: 92px;
            }
            .cm-fc-calendar .fc-daygrid-day-events {
              width: 100%;
              min-width: 0;
              min-height: 28px !important;
              overflow: hidden;
            }
            .cm-fc-calendar .fc-daygrid-event-harness {
              margin-top: 0 !important;
              width: 100% !important;
              max-width: 100% !important;
              /* NB: do NOT force height:100% here. The harness sits inside the
                 row whose height FullCalendar measures in TableRow.updateSizing;
                 a 100% height makes the measured content height depend on the
                 row height it's deriving → infinite setState loop ("Maximum
                 update depth exceeded"). Let events size to their content. */
              height: auto;
              min-width: 0;
              overflow: hidden;
            }
            .cm-fc-calendar .fc-daygrid-event {
              padding: 0;
              margin-bottom: 0.25rem;
              border-radius: 0.375rem;
              width: 100% !important;
              max-width: 100% !important;
              height: auto;
              min-width: 0;
              border: 0;
              background: transparent;
              overflow: hidden;
            }
            /* Inside the event the case label can be long — let the
               container clip and the inner text truncate. */
            .cm-fc-calendar .fc-event-main,
            .cm-fc-calendar .fc-event-main-frame {
              min-width: 0;
              max-width: 100%;
              overflow: hidden;
            }
            .cm-fc-calendar .fc-h-event { background: transparent; border: 0; }
            .cm-fc-calendar .fc-event { cursor: pointer !important; }
            .cm-fc-calendar .fc-event-selected,
            .cm-fc-calendar .fc-event:focus { box-shadow: none; }
            .cm-fc-calendar .fc-event-selected:after,
            .cm-fc-calendar .fc-event:focus:after { background: transparent; }
            .cm-fc-calendar .fc-daygrid-event-harness > .fc-daygrid-event { white-space: normal; }
          `}</style>

          {/* Per-Slot Notes Popup (CM-only, preserved) */}
          {activeNoteSlot && (() => {
            const [day, period] = activeNoteSlot.split('-');
            const notes = slotNotes[activeNoteSlot] || [];
            const dateStr = `${MONTH_NAMES[currentMonth]} ${day}, ${currentYear}`;
            const periodLabel = period === 'am' ? 'Morning' : 'Afternoon';
            return (
              <div
                data-note-popup
                ref={notePopupRef}
                className="absolute z-50 w-[340px] rounded-[10px] bg-white overflow-hidden"
                style={{ boxShadow: '0 4px 24px rgba(0,0,0,0.14)', top: notePopupPos.top, left: Math.max(0, notePopupPos.left), visibility: notePopupReady ? 'visible' : 'hidden' }}
              >
                <div className="flex items-center gap-2.5 px-5 py-3.5 bg-[#115e59] rounded-t-[10px]">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M2 2h12v10H6l-4 3V2z" stroke="white" strokeWidth="1.2" strokeLinejoin="round" fill="none" />
                    <path d="M5 6h6M5 9h4" stroke="white" strokeWidth="1" strokeLinecap="round" />
                  </svg>
                  <span className="text-[14px] font-semibold text-white font-['Inter']">Slot Notes</span>
                  <div className="flex-1" />
                  <span className="text-[11px] font-normal text-white/70 font-['Inter']">{dateStr} · {periodLabel}</span>
                  <button onClick={() => setActiveNoteSlot(null)} className="ml-1 hover:bg-white/10 rounded p-0.5">
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                      <path d="M4 4L12 12M12 4L4 12" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
                    </svg>
                  </button>
                </div>
                <div className="flex flex-col gap-3 px-5 py-4 max-h-[260px] overflow-auto">
                  {notes.length === 0 && (
                    <p className="text-[12px] text-[#999] font-['Inter'] text-center py-2">No notes yet for this slot</p>
                  )}
                  {notes.map((note, i) => (
                    <div key={i} className="flex flex-col gap-1 rounded-lg bg-[#f7f7f5] px-3.5 py-2.5">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1.5">
                          <div className="w-5 h-5 rounded-full bg-[#115e59] flex items-center justify-center">
                            <span className="text-[8px] font-bold text-white">{note.author?.[0] || 'A'}</span>
                          </div>
                          <span className="text-[11px] font-semibold text-[#555] font-['Inter']">{note.author}</span>
                        </div>
                        <span className="text-[10px] text-[#999] font-['Inter']">{note.timestamp}</span>
                      </div>
                      <p className="text-[12px] text-[#333] font-['Inter'] leading-[1.5] pl-[26px]">{note.text}</p>
                    </div>
                  ))}
                </div>
                <div className="border-t border-[#e8e8e8] px-5 py-3">
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={noteInput}
                      onChange={(e) => setNoteInput(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleAddNote()}
                      placeholder="Add a note..."
                      className="flex-1 h-[36px] px-3 rounded-md bg-[#f5f5f5] text-[13px] text-[#333] placeholder-[#aaa] font-['Inter'] outline-none focus:ring-1 focus:ring-[#115e59]/30"
                    />
                    <button
                      onClick={handleAddNote}
                      disabled={!noteInput.trim() || savingNote}
                      className={`flex items-center justify-center w-[36px] h-[36px] rounded-md transition-colors ${noteInput.trim() && !savingNote ? 'bg-[#115e59] hover:bg-[#0d4f4a]' : 'bg-[#e5e5e5]'}`}
                    >
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                        <path d="M1 7L12 1L7 13L6 8L1 7Z" stroke="white" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
      </div>

      {/* Add Activity. One dialog for both doors — the header button and the
          per-day control — with `activityDate` saying which. `key` remounts the
          form per day so opening a different date starts clean. */}
      {activityAvailable && (activityDate !== null || editingActivity) && (
        <div className="fixed inset-0 z-[100] flex justify-center overflow-y-auto py-8">
          <div
            /* FIXED, not absolute: this sits inside the scrolling overlay, and
               `absolute inset-0` only covers that container's first viewport —
               scroll a tall dialog up and the dim stops, showing the page
               underneath through the gap. */
            className="fixed inset-0 bg-black/40"
            onClick={closeActivityDialog}
          />
          {/* 670px, and NO heading here: the form owns its own title, subtitle
              and footer, and renders edge to edge by cancelling this box's
              p-6. A heading in the wrapper printed "Add activity" twice, and
              560px squeezed the date and time columns until the clock was
              clipped mid-placeholder. */}
          <div className="relative z-10 my-auto w-[670px] max-w-[94vw] rounded-xl bg-white p-6 shadow-2xl">
            <button
              onClick={closeActivityDialog}
              aria-label="Close"
              className="absolute right-5 top-5 z-10 rounded p-1 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M18 6 6 18M6 6l12 12" />
              </svg>
            </button>
            <AddActivityForm
              key={editingActivity?.id || activityDate || 'today'}
              mediatorId={mediatorParamOf(selectedMediator)}
              activity={editingActivity}
              initialDate={activityDate}
              onCancel={closeActivityDialog}
              onSaved={(saved, result) => {
                closeActivityDialog();
                /* So the chip appears or updates straight away. */
                loadActivities();
                /* "Close it off" wrote to AVAILABILITY, which loadActivities
                   does not touch — without this the slot keeps rendering as
                   Available until the month is paged or reloaded. */
                if (result?.slotClosed) refreshSlots();
              }}
            />
          </div>
        </div>
      )}

      {/* Deleting from the chip's "…" menu. Deliberately the same card, the
          same wording and the same two buttons as the confirm inside the
          Activities dialog, so what a case manager reads before removing an
          activity does not depend on where they started. */}
      <ConfirmDialog
        isOpen={!!deletingActivity}
        title="Delete Activity"
        cancelText="Cancel"
        confirmText={isDeletingActivity ? 'Deleting...' : 'Delete'}
        tone="danger"
        loading={isDeletingActivity}
        onCancel={() => setDeletingActivity(null)}
        onConfirm={deleteActivity}
      >
        Are you sure you want to delete{' '}
        <strong>{deletingActivity?.display_title}</strong>? It will be removed
        from the schedule and from the mediator's connected calendar. This
        action cannot be undone.
        {activityError && (
          <span className="mt-2 block text-sm text-red-600">{activityError}</span>
        )}
      </ConfirmDialog>

      {/* Open Monthly Availability — bulk-open modal. Mirrors
          scale-portal/views/schedule/index.jsx Dialog (lines 533-648). */}
      {isMonthlyOpenModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={bulkOpenLoading ? undefined : () => setIsMonthlyOpenModal(false)}
          />
          <div className="relative bg-white rounded-2xl shadow-xl w-[600px] max-w-[calc(100vw-32px)] mx-4 max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-5">
              <h5 className="text-lg font-semibold text-gray-900 tracking-tight mb-4">
                Open Availability — {MONTH_NAMES[currentMonth]} {currentYear}
              </h5>

              <div className="pt-1 flex flex-wrap items-center gap-x-3 gap-y-2 mb-2">
                <span className="inline-flex items-center gap-2 rounded-full bg-slate-800 px-3 py-1.5 text-sm font-medium text-white shadow-sm">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="opacity-90">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                    <line x1="16" y1="2" x2="16" y2="6" />
                    <line x1="8" y1="2" x2="8" y2="6" />
                    <line x1="3" y1="10" x2="21" y2="10" />
                  </svg>
                  <span>
                    {monthlyBulkSlots.length} blocked time slot{monthlyBulkSlots.length === 1 ? '' : 's'}
                  </span>
                </span>
                <span className="text-sm text-gray-500">will be opened</span>
              </div>
              <p className="py-1 text-sm text-gray-500 leading-relaxed mb-6">
                Already booked or open slots won&apos;t be affected.
              </p>

              {/* Case types section — mirrors scale-portal/views/schedule/index.jsx
                  lines 560-624.  Lets the user remove case types they don't
                  want this bulk-open to apply to.  Each selected entry rides
                  along on each toggle call as { id, code }. */}
              {mediatorCoupons.length > 0 && (
                <div className="border-t border-gray-200 pt-3">
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between sm:gap-6">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-gray-900">Case types</p>
                      <p className="mt-1 text-xs text-gray-500 leading-relaxed">
                        Time slots will be opened for the case types below.
                        Remove any you don&apos;t want opened.
                      </p>
                    </div>
                    <div className="flex shrink-0 gap-2 sm:pt-0.5">
                      <button
                        type="button"
                        disabled={bulkOpenLoading || mediatorCoupons.length === 0}
                        onClick={() =>
                          setSelectedMonthlyCoupons(
                            mediatorCoupons.map((c) => ({ value: c.id, label: c.coupon_code }))
                          )
                        }
                        className="text-xs font-medium border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 rounded-md px-2.5 py-1 disabled:opacity-50"
                      >
                        Select all
                      </button>
                      <button
                        type="button"
                        disabled={bulkOpenLoading}
                        onClick={() => setSelectedMonthlyCoupons([])}
                        className="text-xs font-medium border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 rounded-md px-2.5 py-1 disabled:opacity-50"
                      >
                        Clear
                      </button>
                    </div>
                  </div>

                  <div className="mt-4">
                    {/* Inline chip selector — adding a react-select dependency
                        for one screen is overkill, so we render the coupons
                        as togglable pills.  Keyboard-accessible buttons. */}
                    <div className="flex flex-wrap gap-1.5 p-2 border border-gray-200 rounded-md bg-white min-h-[44px]">
                      {selectedMonthlyCoupons.length === 0 && (
                        <span className="text-xs text-gray-400 px-1 py-1">
                          Select case types…
                        </span>
                      )}
                      {selectedMonthlyCoupons.map((opt) => (
                        <span
                          key={opt.value}
                          className="inline-flex items-center gap-1 rounded-md bg-slate-100 px-2 py-1 text-xs font-medium text-slate-800"
                        >
                          {opt.label}
                          <button
                            type="button"
                            disabled={bulkOpenLoading}
                            onClick={() =>
                              setSelectedMonthlyCoupons((prev) =>
                                prev.filter((p) => p.value !== opt.value)
                              )
                            }
                            className="ml-0.5 text-slate-500 hover:text-slate-800 disabled:opacity-50"
                            aria-label={`Remove ${opt.label}`}
                          >
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                              <line x1="18" y1="6" x2="6" y2="18" />
                              <line x1="6" y1="6" x2="18" y2="18" />
                            </svg>
                          </button>
                        </span>
                      ))}
                    </div>
                    {/* Unselected coupons — click to add back */}
                    {(() => {
                      const selectedIds = new Set(selectedMonthlyCoupons.map((o) => o.value));
                      const available = mediatorCoupons.filter((c) => !selectedIds.has(c.id));
                      if (available.length === 0) return null;
                      return (
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {available.map((c) => (
                            <button
                              key={c.id}
                              type="button"
                              disabled={bulkOpenLoading}
                              onClick={() =>
                                setSelectedMonthlyCoupons((prev) => [
                                  ...prev,
                                  { value: c.id, label: c.coupon_code },
                                ])
                              }
                              className="text-xs font-medium border border-dashed border-gray-300 text-gray-600 hover:bg-gray-50 rounded-md px-2 py-1 disabled:opacity-50"
                            >
                              + {c.coupon_code}
                            </button>
                          ))}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              )}

              {bulkOpenFeedback && (
                <div
                  className={`mb-4 rounded-md px-3 py-2 text-sm ${
                    bulkOpenFeedback.type === 'error'
                      ? 'bg-red-50 text-red-700 border border-red-200'
                      : bulkOpenFeedback.type === 'info'
                        ? 'bg-blue-50 text-blue-700 border border-blue-200'
                        : 'bg-green-50 text-green-700 border border-green-200'
                  }`}
                >
                  {bulkOpenFeedback.text}
                </div>
              )}

              <div className="mt-6 flex flex-col-reverse gap-2 border-t border-gray-200 pt-4 sm:flex-row sm:items-center sm:justify-between">
                <button
                  type="button"
                  disabled={bulkOpenLoading}
                  onClick={() => setIsMonthlyOpenModal(false)}
                  className="text-sm font-medium text-gray-600 hover:text-gray-800 px-3 py-2 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={bulkOpenLoading || monthlyBulkSlots.length === 0}
                  onClick={handleOpenMonthlyAvailability}
                  className="inline-flex items-center justify-center gap-2 rounded-md bg-slate-800 hover:bg-slate-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {bulkOpenLoading && (
                    <svg className="animate-spin" width="14" height="14" viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="12" r="10" stroke="rgba(255,255,255,0.3)" strokeWidth="3" />
                      <path d="M22 12a10 10 0 0 0-10-10" stroke="#ffffff" strokeWidth="3" strokeLinecap="round" />
                    </svg>
                  )}
                  Open All
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Availability Report modal. Mirrors scale-portal
          components/template/ScheduleManagement/AvailabilityReportModal.jsx
          (default variant). */}
      {isReportModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={reportLoading ? undefined : () => setIsReportModalOpen(false)}
          />
          <div className="relative bg-white rounded-2xl shadow-xl w-[600px] max-w-[calc(100vw-32px)] mx-4 max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-5">
              <h2 className="text-xl font-semibold mb-4 text-gray-800">Availability Report</h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    MONTHS FROM TODAY
                  </label>
                  <select
                    value={reportMonthsAhead}
                    onChange={(e) => setReportMonthsAhead(parseInt(e.target.value, 10))}
                    className="w-full h-10 px-3 rounded-md border border-gray-300 bg-white text-sm text-gray-800 outline-none focus:border-teal-700 focus:ring-1 focus:ring-teal-700/30"
                  >
                    {Array.from({ length: 12 }, (_, i) => i + 1).map((n) => (
                      <option key={n} value={n}>{n}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">DAYS</label>
                  <div className="flex gap-4 flex-wrap">
                    {[
                      { key: 'Monday', label: 'Mon' },
                      { key: 'Tuesday', label: 'Tue' },
                      { key: 'Wednesday', label: 'Wed' },
                      { key: 'Thursday', label: 'Thu' },
                      { key: 'Friday', label: 'Fri' },
                    ].map((d) => (
                      <label
                        key={d.key}
                        className="inline-flex items-center gap-2 text-sm font-medium text-gray-700 cursor-pointer select-none"
                      >
                        <input
                          type="checkbox"
                          checked={!!reportSelectedDays[d.key]}
                          onChange={(e) =>
                            setReportSelectedDays((prev) => ({ ...prev, [d.key]: e.target.checked }))
                          }
                          className="h-4 w-4 rounded border-gray-300 text-teal-700 focus:ring-teal-700"
                        />
                        {d.label}
                      </label>
                    ))}
                  </div>
                </div>

                <div className="my-4 h-px w-full bg-slate-200/90" role="separator" aria-hidden />

                {reportFeedback && (
                  <div
                    className={`rounded-md px-3 py-2 text-sm ${
                      reportFeedback.type === 'error'
                        ? 'bg-red-50 text-red-700 border border-red-200'
                        : reportFeedback.type === 'warning'
                          ? 'bg-amber-50 text-amber-700 border border-amber-200'
                          : reportFeedback.type === 'info'
                            ? 'bg-blue-50 text-blue-700 border border-blue-200'
                            : 'bg-green-50 text-green-700 border border-green-200'
                    }`}
                  >
                    {reportFeedback.text}
                  </div>
                )}

                {reportText && (
                  <div className="mt-4">
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-semibold text-gray-700">Offered Dates</label>
                      <button
                        type="button"
                        onClick={handleCopyReport}
                        className="inline-flex items-center gap-1.5 rounded-md bg-teal-50 hover:bg-teal-100 px-3 py-1.5 text-xs font-semibold text-teal-700 border border-teal-200"
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                        </svg>
                        Copy Report
                      </button>
                    </div>
                    <textarea
                      ref={reportTextareaRef}
                      readOnly
                      value={reportText}
                      className="w-full h-64 p-3 border border-gray-300 rounded text-sm font-mono bg-gray-50 resize-none overflow-y-auto"
                      style={{ whiteSpace: 'pre-wrap' }}
                    />
                  </div>
                )}

                <div className="flex justify-end gap-3 mt-6">
                  <button
                    type="button"
                    onClick={() => setIsReportModalOpen(false)}
                    disabled={reportLoading}
                    className="inline-flex items-center justify-center rounded-md bg-gray-100 hover:bg-gray-200 px-4 py-2 text-sm font-semibold text-gray-700 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={generateAvailabilityReport}
                    disabled={reportLoading}
                    className="inline-flex items-center justify-center gap-2 rounded-md bg-teal-700 hover:bg-teal-800 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {reportLoading && (
                      <svg className="animate-spin" width="14" height="14" viewBox="0 0 24 24" fill="none">
                        <circle cx="12" cy="12" r="10" stroke="rgba(255,255,255,0.3)" strokeWidth="3" />
                        <path d="M22 12a10 10 0 0 0-10-10" stroke="#ffffff" strokeWidth="3" strokeLinecap="round" />
                      </svg>
                    )}
                    GO
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
